import React, { useEffect, useState } from "react";
import { useStoreState } from "easy-peasy";
import { css } from "@emotion/react";
import { motion } from "framer-motion";
import { FadeLoader } from "react-spinners";
import { fetchApplications } from "../api/applications";
import { APPLICATIONS_METADATA } from "../lib/applications";
import { HR_APP_URL } from "../constants";
import { ApplicationCard } from "../components";
import { TwoColumnLayout } from "../templates";
import { settings, animations } from "../settings";

const APPLICATION_ORDER = ["hr", "rta", "partner-hub", "saas"];

export const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const profile = useStoreState((state) => state.profile);

  const { animate } = settings;
  const { form } = animations;

  useEffect(() => {
    setIsLoading(true);
    fetchApplications()
      .then((response) => {
        setApplications(response.data.applications);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleApplicationClick = (application) => {
    setSelectedApp(application);
    const applicationDetail = APPLICATIONS_METADATA[application];
    if (applicationDetail?.url) {
      setTimeout(() => {
        window.location = applicationDetail.url;
      }, 300);
    }
  };

  const handleLogout = () => {
    window.location = "/logout";
  };

  const greetingCSS = css`
    display: block;
    margin: 25px auto;
  `;

  const leftContent = (
    <div className="absolute inset-0">
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet="/design/images/login-page/launch-pad-desktop.webp"
        />
        <img
          src="/design/images/login-page/launch-pad-tablet.webp"
          alt="Launch pad illustration"
          className="w-full h-full object-cover"
        />
      </picture>
      <div className="absolute z-10" style={{ top: '2.5rem', left: '2.5rem' }}>
        <img
          src="/design/images/login-page/breathe_logo_white.svg"
          alt="Breathe logo icon"
          className="h-8 w-9"
        />
      </div>
    </div>
  );

  if (!profile || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FadeLoader color="#0580fa" loading css={greetingCSS} />
      </div>
    );
  }

  const logoutButton = (
    <button
      type="button"
      onClick={handleLogout}
      className="px-4 py-2 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all duration-200 cursor-pointer"
      style={{
        borderRadius: "8px",
        border: "1px solid #E5E5E5",
        background: "#FFF",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        fontSize: "14px",
        color: "#0A0A0A",
        lineHeight: "100%",
      }}
    >
      Log out
    </button>
  );

  return (
    <TwoColumnLayout
      leftContent={leftContent}
      rightContentHeader={logoutButton}
      contentMaxWidth={applications.length > 0 ? 640 : null}
    >
      <div className="flex-1 flex items-center justify-center">
        <motion.div
          variants={form}
          initial={animate ? "hidden" : ""}
          animate={animate ? "show" : ""}
          className="w-full"
        >
          {applications.length === 0 ? (
            <>
              <div className="mb-5">
                <h1 className="heading-responsive font-medium text-black tracking-tight mb-2">
                  You don&apos;t have access
                </h1>
              </div>
              <div className="mb-6">
                <p className="text-sm login-text-foreground font-inter leading-relaxed">
                  It doesn&apos;t look like you have access to any applications yet. You can sign up in just a few clicks.
                </p>
              </div>
              <a
                href={`${HR_APP_URL}/signup`}
                className="w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter no-underline"
              >
                Sign up here
              </a>
            </>
          ) : (
            <>
              <div className="mb-5">
                <h1 className="text-2xl font-medium text-black tracking-tight">
                  Welcome back {profile.first_name}
                </h1>
                <p className="text-gray-600">
                  Where do you want to go?
                </p>
              </div>
              <div className={`grid gap-4 md:gap-6 ${
                applications.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
              >
                {[...applications]
                  .sort((a, b) => APPLICATION_ORDER.indexOf(a) - APPLICATION_ORDER.indexOf(b))
                  .map((application) => {
                    const applicationDetail = APPLICATIONS_METADATA[application];
                    if (!applicationDetail) return null;

                    return (
                      <ApplicationCard
                        key={application}
                        application={application}
                        name={applicationDetail.name}
                        description={applicationDetail.description}
                        onClick={() => handleApplicationClick(application)}
                        isSelected={selectedApp === application}
                        fullWidth={applications.length === 1}
                      />
                    );
                  })}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </TwoColumnLayout>
  );
};
